import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  HomepageLink,
  UserPreferences,
  CreateLinkInput,
  AutocompleteResult,
  UserInfoResponse,
  HomepageRepo,
  CreateRepoInput,
  RepoAutocompleteResult,
  HomepageGcpProject,
  CreateGcpProjectInput,
  GcpProjectAutocompleteResult,
  ProjectBillingInfo,
  GcpOrganization,
  OrgProject,
} from './types';

export const homepageLinksApiRef = createApiRef<HomepageLinksApi>({
  id: 'plugin.homepage-links.service',
});

export interface HomepageLinksApi {
  // Get links for current user's groups (additionalGroups are merged with JWT groups for querying)
  getLinks(linkType: 'onboarding' | 'team', additionalGroups?: string[]): Promise<HomepageLink[]>;

  // Get links for a specific group
  getLinksByGroup(
    groupRef: string,
    linkType: 'onboarding' | 'team',
  ): Promise<HomepageLink[]>;

  // Create a new link
  createLink(input: CreateLinkInput): Promise<HomepageLink>;

  // Remove link from a group
  removeLinkFromGroup(linkId: string, groupRef: string): Promise<void>;

  // Delete a link entirely
  deleteLink(linkId: string): Promise<void>;

  // Search for existing links (for autocomplete)
  searchLinks(query: string): Promise<AutocompleteResult[]>;

  // Get user preferences
  getPreferences(): Promise<UserPreferences>;

  // Update user preferences
  updatePreferences(
    preferences: Partial<UserPreferences>,
  ): Promise<UserPreferences>;

  // Get current user's groups
  getUserGroups(): Promise<string[]>;

  // Get current user info (userRef and groups)
  getUserInfo(): Promise<UserInfoResponse>;
}

export class HomepageLinksClient implements HomepageLinksApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('homepage-links');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);

    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Request failed with status ${response.status}: ${text}`,
      );
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async getLinks(linkType: 'onboarding' | 'team', additionalGroups?: string[]): Promise<HomepageLink[]> {
    let url = `/links?type=${linkType}`;
    if (additionalGroups?.length) {
      url += `&groups=${additionalGroups.map(encodeURIComponent).join(',')}`;
    }
    return this.fetch<HomepageLink[]>(url);
  }

  async getLinksByGroup(
    groupRef: string,
    linkType: 'onboarding' | 'team',
  ): Promise<HomepageLink[]> {
    const encodedGroup = encodeURIComponent(groupRef);
    return this.fetch<HomepageLink[]>(
      `/links/by-group/${encodedGroup}?type=${linkType}`,
    );
  }

  async createLink(input: CreateLinkInput): Promise<HomepageLink> {
    return this.fetch<HomepageLink>('/links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async removeLinkFromGroup(linkId: string, groupRef: string): Promise<void> {
    const encodedGroup = encodeURIComponent(groupRef);
    await this.fetch(`/links/${linkId}/groups/${encodedGroup}`, {
      method: 'DELETE',
    });
  }

  async deleteLink(linkId: string): Promise<void> {
    await this.fetch(`/links/${linkId}`, {
      method: 'DELETE',
    });
  }

  async searchLinks(query: string): Promise<AutocompleteResult[]> {
    if (!query || query.length < 2) {
      return [];
    }
    return this.fetch<AutocompleteResult[]>(
      `/autocomplete?q=${encodeURIComponent(query)}`,
    );
  }

  async getPreferences(): Promise<UserPreferences> {
    return this.fetch<UserPreferences>('/preferences');
  }

  async updatePreferences(
    preferences: Partial<UserPreferences>,
  ): Promise<UserPreferences> {
    return this.fetch<UserPreferences>('/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(preferences),
    });
  }

  async getUserGroups(): Promise<string[]> {
    const response = await this.fetch<UserInfoResponse>('/user-groups');
    return response.groups;
  }

  async getUserInfo(): Promise<UserInfoResponse> {
    return this.fetch<UserInfoResponse>('/user-groups');
  }
}

// Homepage Repos API for saved GitHub repositories
export const homepageReposApiRef = createApiRef<HomepageReposApi>({
  id: 'plugin.homepage-repos.service',
});

export interface HomepageReposApi {
  // Get saved repos for current user's groups (additionalGroups are merged with JWT groups for querying)
  getRepos(additionalGroups?: string[]): Promise<HomepageRepo[]>;

  // Create/add a new repo
  createRepo(input: CreateRepoInput): Promise<HomepageRepo>;

  // Remove repo from a group
  removeRepoFromGroup(repoId: string, groupRef: string): Promise<void>;

  // Delete a repo entirely
  deleteRepo(repoId: string): Promise<void>;

  // Search for existing saved repos (for autocomplete)
  searchRepos(query: string): Promise<RepoAutocompleteResult[]>;

  // Get current user info (userRef and groups)
  getUserInfo(): Promise<UserInfoResponse>;
}

export class HomepageReposClient implements HomepageReposApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('homepage-repos');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);

    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Request failed with status ${response.status}: ${text}`,
      );
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async getRepos(additionalGroups?: string[]): Promise<HomepageRepo[]> {
    let url = '/repos';
    if (additionalGroups?.length) {
      url += `?groups=${additionalGroups.map(encodeURIComponent).join(',')}`;
    }
    return this.fetch<HomepageRepo[]>(url);
  }

  async createRepo(input: CreateRepoInput): Promise<HomepageRepo> {
    return this.fetch<HomepageRepo>('/repos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async removeRepoFromGroup(repoId: string, groupRef: string): Promise<void> {
    const encodedGroup = encodeURIComponent(groupRef);
    await this.fetch(`/repos/${repoId}/groups/${encodedGroup}`, {
      method: 'DELETE',
    });
  }

  async deleteRepo(repoId: string): Promise<void> {
    await this.fetch(`/repos/${repoId}`, {
      method: 'DELETE',
    });
  }

  async searchRepos(query: string): Promise<RepoAutocompleteResult[]> {
    if (!query || query.length < 2) {
      return [];
    }
    return this.fetch<RepoAutocompleteResult[]>(
      `/autocomplete?q=${encodeURIComponent(query)}`,
    );
  }

  async getUserInfo(): Promise<UserInfoResponse> {
    return this.fetch<UserInfoResponse>('/user-groups');
  }
}

// Homepage GCP Projects API for saved GCP projects
export const homepageGcpProjectsApiRef = createApiRef<HomepageGcpProjectsApi>({
  id: 'plugin.homepage-gcp-projects.service',
});

export interface HomepageGcpProjectsApi {
  // Get saved GCP projects for current user's groups (additionalGroups are merged with JWT groups for querying)
  getProjects(additionalGroups?: string[]): Promise<HomepageGcpProject[]>;

  // Create/add a new GCP project
  createProject(input: CreateGcpProjectInput): Promise<HomepageGcpProject>;

  // Remove project from a group
  removeProjectFromGroup(projectId: string, groupRef: string): Promise<void>;

  // Delete a project entirely
  deleteProject(projectId: string): Promise<void>;

  // Search for existing saved projects (for autocomplete)
  searchProjects(query: string): Promise<GcpProjectAutocompleteResult[]>;

  // Get current user info (userRef and groups)
  getUserInfo(): Promise<UserInfoResponse>;

  // Get billing info for a single project
  getProjectBilling(gcpProjectId: string, googleAccessToken: string): Promise<ProjectBillingInfo | null>;

  // Get billing info for multiple projects
  getProjectsBilling(gcpProjectIds: string[], googleAccessToken: string): Promise<Record<string, ProjectBillingInfo>>;

  // Get organizations the user has access to
  getOrganizations(googleAccessToken: string): Promise<GcpOrganization[]>;

  // Get all projects in an organization
  getOrgProjects(googleAccessToken: string, orgId: string): Promise<OrgProject[]>;
}

export class HomepageGcpProjectsClient implements HomepageGcpProjectsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('homepage-gcp-projects');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);

    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Request failed with status ${response.status}: ${text}`,
      );
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async getProjects(additionalGroups?: string[]): Promise<HomepageGcpProject[]> {
    let url = '/projects';
    if (additionalGroups?.length) {
      url += `?groups=${additionalGroups.map(encodeURIComponent).join(',')}`;
    }
    return this.fetch<HomepageGcpProject[]>(url);
  }

  async createProject(input: CreateGcpProjectInput): Promise<HomepageGcpProject> {
    return this.fetch<HomepageGcpProject>('/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async removeProjectFromGroup(projectId: string, groupRef: string): Promise<void> {
    const encodedGroup = encodeURIComponent(groupRef);
    await this.fetch(`/projects/${projectId}/groups/${encodedGroup}`, {
      method: 'DELETE',
    });
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.fetch(`/projects/${projectId}`, {
      method: 'DELETE',
    });
  }

  async searchProjects(query: string): Promise<GcpProjectAutocompleteResult[]> {
    if (!query || query.length < 2) {
      return [];
    }
    return this.fetch<GcpProjectAutocompleteResult[]>(
      `/autocomplete?q=${encodeURIComponent(query)}`,
    );
  }

  async getUserInfo(): Promise<UserInfoResponse> {
    return this.fetch<UserInfoResponse>('/user-groups');
  }

  async getProjectBilling(gcpProjectId: string, googleAccessToken: string): Promise<ProjectBillingInfo | null> {
    return this.fetch<ProjectBillingInfo | null>(`/billing/${encodeURIComponent(gcpProjectId)}`, {
      headers: { 'X-Gcp-Access-Token': googleAccessToken },
    });
  }

  async getProjectsBilling(gcpProjectIds: string[], googleAccessToken: string): Promise<Record<string, ProjectBillingInfo>> {
    if (gcpProjectIds.length === 0) {
      return {};
    }
    return this.fetch<Record<string, ProjectBillingInfo>>('/billing/batch', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Gcp-Access-Token': googleAccessToken,
      },
      body: JSON.stringify({ projectIds: gcpProjectIds }),
    });
  }

  async getOrganizations(googleAccessToken: string): Promise<GcpOrganization[]> {
    return this.fetch<GcpOrganization[]>('/organizations', {
      headers: { 'X-Gcp-Access-Token': googleAccessToken },
    });
  }

  async getOrgProjects(googleAccessToken: string, orgId: string): Promise<OrgProject[]> {
    return this.fetch<OrgProject[]>(`/organizations/${encodeURIComponent(orgId)}/projects`, {
      headers: { 'X-Gcp-Access-Token': googleAccessToken },
    });
  }
}
